import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  AlignBottomIcon,
  AlignCenterHorizontallyIcon,
  AlignCenterVerticallyIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
  ColorWheelIcon,
  MixerHorizontalIcon,
} from '@radix-ui/react-icons';
import { Select } from '@radix-ui/themes';
import { useUIStore, useMovieStore, useSelectionStore, useTimelineStore, useEditorStore } from '../../stores';
import { useSignalR } from '../../hooks';
import { Toolpad } from './Toolpad';
import { SidebarColumn, type SidebarRailItem } from './SidebarColumn';
import type {
  FillMode,
  SceneObject,
  EasingType,
  Keyframe,
  KeyframeState,
  StrokeSide,
  ToolpadId,
} from '../../types';
import {
  ALL_STROKE_SIDES,
  DEFAULT_FILL_GRADIENT,
  DEFAULT_OBJECT_GLOW,
} from '../../types';
import { generateId } from '../../util/utils';
import { resolveStateTarget } from '../../util/resolveStateTarget';
import { seedNewKeyframeState } from '../../util/newKeyframeSeed';
import { PropertyInput } from '../ui/PropertyInput';
import { serializeShapeBody, shapeBodyFromSceneObject } from '../../util/shapeBody';
import {
  isBorderNone,
  isFillNone,
  serializeTextBody,
  textBodyFromSceneObject,
} from '../../util/textBody';
import { serializeImageBodyForPersist } from '../../util/imageBody';
import {
  mergeFillGradient,
  mergeObjectGlow,
  mergeObjectShadow,
  resolveFillMode,
} from '../../util/objectVisualStyle';
import {
  hasAnyStrokeSide,
  resolveStrokeSides,
  supportsStrokeSides,
  toggleStrokeSide,
} from '../../util/strokeSides';
import { useTranslation } from '../../i18n';
import type { MessageKey } from '../../i18n/types';
import { keyframeStatePatchJson, sceneObjectTransformPatchJson } from '../../util/sceneObjectLayoutProperties';

const RIGHT_TOOLPAD_ORDER: Record<string, number> = {
  stylePad: 0,
  objectProperties: 1,
  alignPad: 2,
};

const RIGHT_RAIL_META: Partial<
  Record<ToolpadId, { icon: ReactNode; titleKey: MessageKey }>
> = {
  stylePad: {
    icon: <ColorWheelIcon width={16} height={16} />,
    titleKey: 'rightSidebar.style.title',
  },
  objectProperties: {
    icon: <MixerHorizontalIcon width={16} height={16} />,
    titleKey: 'rightSidebar.properties.title',
  },
  alignPad: {
    icon: <AlignCenterHorizontallyIcon width={16} height={16} />,
    titleKey: 'rightSidebar.align.title',
  },
};

export function RightSidebar() {
  const {
    toolpads,
    rightSidebarOpen,
    setRightSidebar,
    openToolpad,
  } = useUIStore();

  const rightToolpads = Object.values(toolpads)
    .filter((tp) => tp.position === 'right')
    .sort((a, b) => {
      const aRank = RIGHT_TOOLPAD_ORDER[a.id] ?? 100;
      const bRank = RIGHT_TOOLPAD_ORDER[b.id] ?? 100;
      return aRank - bRank;
    });

  const railItems: SidebarRailItem[] = rightToolpads.flatMap((tp) => {
    const meta = RIGHT_RAIL_META[tp.id];
    if (!meta) return [];
    return [{ id: tp.id, icon: meta.icon, titleKey: meta.titleKey }];
  });

  const handleExpandToolpad = (id: ToolpadId) => {
    openToolpad(id);
    setRightSidebar(true);
  };

  return (
    <SidebarColumn
      side="right"
      isOpen={rightSidebarOpen}
      onCollapse={() => setRightSidebar(false)}
      onExpandToolpad={handleExpandToolpad}
      items={railItems}
    >
      {rightToolpads.map((tp) => (
        <ToolpadWrapper key={tp.id} toolpadId={tp.id} />
      ))}
    </SidebarColumn>
  );
}

function ToolpadWrapper({ toolpadId }: { toolpadId: string }) {
  switch (toolpadId) {
    case 'stylePad':
      return <StyleToolpad />;
    case 'objectProperties':
      return <ObjectPropertiesToolpad />;
    case 'alignPad':
      return <AlignToolpad />;
    default:
      return null;
  }
}

type PropertyTab = 'object' | 'tween';

const EASING_OPTIONS: EasingType[] = [
  'linear',
  'easeIn',
  'easeOut',
  'easeInOut',
  'bounce',
  'elastic',
  'back',
];

const EASING_LABEL_KEYS: Record<EasingType, MessageKey> = {
  linear: 'rightSidebar.properties.linear',
  easeIn: 'rightSidebar.properties.easeIn',
  easeOut: 'rightSidebar.properties.easeOut',
  easeInOut: 'rightSidebar.properties.easeInOut',
  bounce: 'rightSidebar.properties.bounce',
  elastic: 'rightSidebar.properties.elastic',
  back: 'rightSidebar.properties.back',
};

function StyleToolpad() {
  const { t } = useTranslation();
  const { toolpads, toggleToolpad } = useUIStore();
  const { currentScene, updateSceneObject } = useMovieStore();
  const { selectedIds, focusedId } = useSelectionStore();
  const { fillColor: defaultFillColor, strokeColor: defaultStrokeColor, strokeWidth: defaultStrokeWidth, strokeStyle: defaultStrokeStyle } = useEditorStore();
  const { sceneObjectUpdateContent, connect, status: signalRStatus } = useSignalR(false);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFillColorRef = useRef(defaultFillColor);

  const isOpen = toolpads.stylePad.isOpen;
  const activeSelectedId =
    focusedId && selectedIds.includes(focusedId) ? focusedId : selectedIds[0];
  const selectedObject = activeSelectedId
    ? currentScene?.objects.find((o) => o.id === activeSelectedId)
    : undefined;
  const isShape = selectedObject?.type === 'shape';
  const isText = selectedObject?.type === 'text';
  const isImage = selectedObject?.type === 'image';
  const supportsFill = isShape || isText;
  const supportsEffects = isShape || isText || isImage;
  const supportsStylePanel = supportsFill || supportsEffects;

  const fill = selectedObject?.fill ?? defaultFillColor;
  const stroke = selectedObject?.stroke ?? defaultStrokeColor;
  const strokeWidth = selectedObject?.strokeWidth ?? defaultStrokeWidth;
  const strokeStyle = selectedObject?.strokeStyle ?? defaultStrokeStyle;
  const fontSize = selectedObject?.fontSize ?? 16;
  const textAlign = selectedObject?.textAlign ?? 'center';
  const strokeSides = resolveStrokeSides(selectedObject?.strokeSides);
  const fillMode = selectedObject ? resolveFillMode(selectedObject) : 'solid';
  const fillGradient = mergeFillGradient(selectedObject?.fill, selectedObject?.fillGradient);
  const shadow = mergeObjectShadow(selectedObject?.shadow);
  const glow = mergeObjectGlow(selectedObject?.glow);
  const isBoxShape =
    isShape &&
    supportsStrokeSides(selectedObject?.shapeType) &&
    selectedObject?.shapeType !== 'path' &&
    selectedObject?.shapeType !== 'triangle';

  const fillIsNone = selectedObject ? isFillNone(selectedObject) : true;
  const borderIsNone = selectedObject ? isBorderNone(selectedObject) : true;

  const [styleHint, setStyleHint] = useState<string | null>(null);

  useEffect(() => {
    if (!fillIsNone && fill !== 'none') {
      lastFillColorRef.current = fill;
    }
  }, [fill, fillIsNone]);

  useEffect(() => {
    setStyleHint(null);
  }, [selectedObject?.id]);

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);

  const persistObjectStyle = useCallback(async (object: SceneObject) => {
    const sceneObjectId = Number.parseInt(object.id, 10);
    if (!Number.isFinite(sceneObjectId)) return;

    const body =
      object.type === 'shape'
        ? serializeShapeBody(shapeBodyFromSceneObject(object))
        : object.type === 'text'
          ? serializeTextBody(textBodyFromSceneObject(object))
          : object.type === 'image'
            ? serializeImageBodyForPersist(object)
            : null;
    if (body == null) return;

    try {
      if (signalRStatus !== 'Connected') await connect();
      await sceneObjectUpdateContent({
        id: sceneObjectId,
        type: object.type,
        body,
      });
    } catch (error) {
      console.error('[StyleToolpad] Failed to persist object style:', error);
    }
  }, [connect, sceneObjectUpdateContent, signalRStatus]);

  const schedulePersistStyle = useCallback((nextObject: SceneObject) => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      void persistObjectStyle(nextObject);
    }, 300);
  }, [persistObjectStyle]);

  const applyStylePatch = useCallback((patch: Partial<SceneObject>) => {
    if (
      !selectedObject ||
      (selectedObject.type !== 'shape' &&
        selectedObject.type !== 'text' &&
        selectedObject.type !== 'image')
    ) {
      return;
    }
    const nextObject = { ...selectedObject, ...patch };
    updateSceneObject(selectedObject.id, patch);
    schedulePersistStyle(nextObject);
  }, [selectedObject, updateSceneObject, schedulePersistStyle]);

  const setFillColor = (value: string) => {
    setStyleHint(null);
    applyStylePatch({
      fill: value,
      fillMode: fillMode === 'none' ? 'solid' : fillMode,
      fillGradient:
        fillMode === 'gradient'
          ? { ...fillGradient, colorStart: value }
          : selectedObject?.fillGradient,
    });
  };

  const setFillModeValue = (mode: FillMode) => {
    if (mode === 'none') {
      if (isShape && borderIsNone) {
        setStyleHint(t('rightSidebar.style.fillBorderConflict'));
        return;
      }
      setStyleHint(null);
      applyStylePatch({ fillMode: 'none', fill: 'none' });
      return;
    }
    setStyleHint(null);
    if (mode === 'gradient') {
      const start = lastFillColorRef.current || defaultFillColor;
      applyStylePatch({
        fillMode: 'gradient',
        fill: start,
        fillGradient: {
          ...DEFAULT_FILL_GRADIENT,
          ...selectedObject?.fillGradient,
          colorStart: selectedObject?.fillGradient?.colorStart ?? start,
          colorEnd: selectedObject?.fillGradient?.colorEnd ?? DEFAULT_FILL_GRADIENT.colorEnd,
        },
      });
      return;
    }
    applyStylePatch({
      fillMode: 'solid',
      fill: lastFillColorRef.current || defaultFillColor,
    });
  };

  const setGradientColorEnd = (value: string) => {
    setStyleHint(null);
    applyStylePatch({
      fillMode: 'gradient',
      fillGradient: { ...fillGradient, colorEnd: value },
    });
  };

  const setGradientAngle = (angle: number) => {
    setStyleHint(null);
    applyStylePatch({
      fillMode: 'gradient',
      fillGradient: { ...fillGradient, angle },
    });
  };

  const setStrokeColor = (value: string) => {
    setStyleHint(null);
    applyStylePatch({ stroke: value, strokeStyle: strokeStyle === 'none' ? 'solid' : strokeStyle });
  };

  const setStrokeWidth = (value: number) => {
    setStyleHint(null);
    const width = Math.max(0, value);
    applyStylePatch({
      strokeWidth: width,
      strokeStyle: width <= 0 ? 'none' : strokeStyle === 'none' ? 'solid' : strokeStyle,
    });
  };

  const setStrokeStyle = (value: SceneObject['strokeStyle']) => {
    if (value === 'none') {
      if (fillIsNone) {
        setStyleHint(t('rightSidebar.style.fillBorderConflict'));
        return;
      }
      setStyleHint(null);
      applyStylePatch({ strokeStyle: 'none', strokeWidth: 0 });
      return;
    }
    setStyleHint(null);
    const nextSides = hasAnyStrokeSide(selectedObject?.strokeSides)
      ? resolveStrokeSides(selectedObject?.strokeSides)
      : { ...ALL_STROKE_SIDES };
    applyStylePatch({
      strokeStyle: value,
      strokeWidth: strokeWidth > 0 ? strokeWidth : defaultStrokeWidth,
      strokeSides: nextSides,
    });
  };

  const setStrokeSide = (side: StrokeSide, enabled: boolean) => {
    if (!selectedObject) return;

    if (borderIsNone && enabled) {
      setStyleHint(null);
      applyStylePatch({
        strokeStyle: 'solid',
        strokeWidth: strokeWidth > 0 ? strokeWidth : defaultStrokeWidth,
        strokeSides: {
          top: side === 'top',
          right: side === 'right',
          bottom: side === 'bottom',
          left: side === 'left',
        },
      });
      return;
    }

    const nextSides = toggleStrokeSide(selectedObject.strokeSides, side, enabled);
    if (!hasAnyStrokeSide(nextSides)) {
      if (fillIsNone) {
        setStyleHint(t('rightSidebar.style.fillBorderConflict'));
        return;
      }
      setStyleHint(null);
      applyStylePatch({ strokeSides: nextSides, strokeStyle: 'none', strokeWidth: 0 });
      return;
    }
    setStyleHint(null);
    applyStylePatch({
      strokeSides: nextSides,
      strokeStyle: strokeStyle === 'none' ? 'solid' : strokeStyle,
      strokeWidth: strokeWidth > 0 ? strokeWidth : defaultStrokeWidth,
    });
  };

  const setFontSize = (value: number) => {
    applyStylePatch({ fontSize: Math.max(3, Math.min(200, value)) });
  };

  const setTextAlign = (value: SceneObject['textAlign']) => {
    applyStylePatch({ textAlign: value });
  };

  const setShadowEnabled = (enabled: boolean) => {
    applyStylePatch({
      shadow: { ...shadow, enabled },
    });
  };

  const patchShadow = (patch: Partial<typeof shadow>) => {
    applyStylePatch({
      shadow: { ...shadow, enabled: true, ...patch },
    });
  };

  const setGlowEnabled = (enabled: boolean) => {
    applyStylePatch({
      glow: { ...glow, enabled },
    });
  };

  const patchGlow = (patch: Partial<typeof glow>) => {
    applyStylePatch({
      glow: { ...glow, enabled: true, ...patch },
    });
  };

  const fillColorValue =
    fillMode === 'none' ? lastFillColorRef.current : fill === 'none' ? lastFillColorRef.current : fill;

  return (
    <Toolpad
      title={t('rightSidebar.style.title')}
      isOpen={isOpen}
      onToggle={() => toggleToolpad('stylePad')}
    >
      {!selectedObject && (
        <div className="property-section">
          <div className="no-selection-message">
            {t('rightSidebar.style.selectObject')}
          </div>
        </div>
      )}

      {selectedObject && !supportsStylePanel && (
        <div className="property-section">
          <div className="no-selection-message">
            {t('rightSidebar.style.shapeOrTextOnly')}
          </div>
        </div>
      )}

      {isText && (
        <div className="property-section">
          <div className="property-section-title">{t('rightSidebar.style.text')}</div>
          <div className="keyframe-info" style={{ gap: 8 }}>
            <div className="property-row-full">
              <label className="label">{t('rightSidebar.style.fontSize')}</label>
              <div className="property-input-with-slider">
                <input
                  type="range"
                  min={3}
                  max={200}
                  value={fontSize}
                  onChange={(e) => setFontSize(Number.parseInt(e.target.value, 10))}
                  aria-label={t('rightSidebar.style.fontSizeAria')}
                />
                <PropertyInput
                  className="property-input-inline"
                  value={fontSize}
                  min={3}
                  max={200}
                  onChange={setFontSize}
                  aria-label={t('rightSidebar.style.fontSizePxAria')}
                />
                <span className="unit">px</span>
              </div>
            </div>
            <div className="property-row-full">
              <label className="label">{t('rightSidebar.style.align')}</label>
              <Select.Root
                value={textAlign}
                onValueChange={(value) => setTextAlign(value as SceneObject['textAlign'])}
                size="1"
              >
                <Select.Trigger
                  variant="surface"
                  className="style-select-trigger"
                  aria-label={t('rightSidebar.style.textAlignAria')}
                />
                <Select.Content position="popper" sideOffset={4}>
                  <Select.Item value="left">{t('rightSidebar.style.left')}</Select.Item>
                  <Select.Item value="center">{t('rightSidebar.style.center')}</Select.Item>
                  <Select.Item value="right">{t('rightSidebar.style.right')}</Select.Item>
                  <Select.Item value="justify">{t('rightSidebar.style.justify')}</Select.Item>
                </Select.Content>
              </Select.Root>
            </div>
          </div>
        </div>
      )}

      {supportsFill && (
        <div className="property-section">
          <div className="property-section-title">{t('rightSidebar.style.colors')}</div>
          <div className="keyframe-info" style={{ gap: 8 }}>
            <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className="color-style-label">{t('rightSidebar.style.fill')}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Select.Root
                  value={fillMode}
                  onValueChange={(value) => setFillModeValue(value as FillMode)}
                  size="1"
                >
                  <Select.Trigger
                    variant="surface"
                    className="style-select-trigger style-select-trigger--compact"
                    aria-label={t('rightSidebar.style.fillModeAria')}
                  />
                  <Select.Content position="popper" sideOffset={4}>
                    <Select.Item value="solid">{t('rightSidebar.style.fillSolid')}</Select.Item>
                    <Select.Item value="gradient">{t('rightSidebar.style.fillGradient')}</Select.Item>
                    <Select.Item value="none">{t('common.none')}</Select.Item>
                  </Select.Content>
                </Select.Root>
                {fillMode !== 'gradient' && (
                  <input
                    type="color"
                    value={fillColorValue}
                    onChange={(e) => setFillColor(e.target.value)}
                    className="color-style-input"
                    title={t('rightSidebar.style.fillColor')}
                    disabled={fillMode === 'none'}
                  />
                )}
              </div>
            </div>

            {fillMode === 'gradient' && (
              <>
                <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="color-style-label">{t('rightSidebar.style.gradientStart')}</span>
                  <input
                    type="color"
                    value={fillGradient.colorStart}
                    onChange={(e) => setFillColor(e.target.value)}
                    className="color-style-input"
                    title={t('rightSidebar.style.gradientStart')}
                  />
                </div>
                <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="color-style-label">{t('rightSidebar.style.gradientEnd')}</span>
                  <input
                    type="color"
                    value={fillGradient.colorEnd}
                    onChange={(e) => setGradientColorEnd(e.target.value)}
                    className="color-style-input"
                    title={t('rightSidebar.style.gradientEnd')}
                  />
                </div>
                <div className="property-row-full">
                  <label className="label">{t('rightSidebar.style.gradientAngle')}</label>
                  <div className="property-input-with-slider">
                    <input
                      type="range"
                      min={0}
                      max={360}
                      value={fillGradient.angle}
                      onChange={(e) => setGradientAngle(Number.parseInt(e.target.value, 10))}
                      aria-label={t('rightSidebar.style.gradientAngleAria')}
                    />
                    <PropertyInput
                      className="property-input-inline"
                      value={fillGradient.angle}
                      min={0}
                      max={360}
                      onChange={setGradientAngle}
                      aria-label={t('rightSidebar.style.gradientAngleAria')}
                    />
                    <span className="unit">°</span>
                  </div>
                </div>
              </>
            )}

            {isShape && (
              <>
                <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="color-style-label">{t('rightSidebar.style.border')}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Select.Root
                      value={borderIsNone ? 'none' : strokeStyle === 'none' ? 'solid' : strokeStyle}
                      onValueChange={(value) => {
                        if (value === 'none') {
                          setStrokeStyle('none');
                        } else {
                          setStrokeStyle(value as SceneObject['strokeStyle']);
                        }
                      }}
                      size="1"
                    >
                      <Select.Trigger
                        variant="surface"
                        className="style-select-trigger style-select-trigger--compact"
                        aria-label={t('rightSidebar.style.borderTypeAria')}
                      />
                      <Select.Content position="popper" sideOffset={4}>
                        <Select.Item value="solid">{t('rightSidebar.style.solid')}</Select.Item>
                        <Select.Item value="dashed">{t('rightSidebar.style.dashed')}</Select.Item>
                        <Select.Item value="dotted">{t('rightSidebar.style.dotted')}</Select.Item>
                        <Select.Item value="none">{t('common.none')}</Select.Item>
                      </Select.Content>
                    </Select.Root>
                    <input
                      type="color"
                      value={stroke}
                      onChange={(e) => setStrokeColor(e.target.value)}
                      className="color-style-input"
                      title={t('rightSidebar.style.borderColor')}
                      disabled={borderIsNone}
                    />
                  </div>
                </div>
                <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="color-style-label">{t('rightSidebar.style.size')}</span>
                  <PropertyInput
                    className="property-input-inline"
                    value={strokeWidth}
                    min={0}
                    max={40}
                    onChange={setStrokeWidth}
                    disabled={borderIsNone}
                    aria-label={t('rightSidebar.style.borderSizeAria')}
                  />
                </div>
                {supportsStrokeSides(selectedObject?.shapeType) && (
                  <div className="color-style-row style-border-sides-row">
                    <span className="color-style-label">{t('rightSidebar.style.sides')}</span>
                    <div className="style-border-sides" role="group" aria-label={t('rightSidebar.style.sidesAria')}>
                      {(
                        [
                          ['top', 'rightSidebar.style.sideTop'],
                          ['right', 'rightSidebar.style.sideRight'],
                          ['bottom', 'rightSidebar.style.sideBottom'],
                          ['left', 'rightSidebar.style.sideLeft'],
                        ] as const
                      ).map(([side, labelKey]) => {
                        const active = !borderIsNone && strokeSides[side];
                        return (
                          <button
                            key={side}
                            type="button"
                            className={`style-border-side-link ${active ? 'active' : ''}`}
                            aria-pressed={active}
                            onClick={() => setStrokeSide(side, !active)}
                          >
                            {t(labelKey)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {(styleHint || (isShape && fillIsNone && borderIsNone)) && (
              <div className="no-selection-message" style={{ marginTop: 4, color: 'var(--color-warning, #f59e0b)' }}>
                {styleHint ?? t('rightSidebar.style.fillBorderConflict')}
              </div>
            )}
          </div>
        </div>
      )}

      {supportsEffects && (
        <div className="property-section">
          <div className="property-section-title">{t('rightSidebar.style.effects')}</div>
          <div className="keyframe-info" style={{ gap: 8 }}>
            <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className="color-style-label">{t('rightSidebar.style.shadow')}</span>
              <button
                type="button"
                className={`style-border-side-link ${shadow.enabled ? 'active' : ''}`}
                aria-pressed={shadow.enabled}
                onClick={() => setShadowEnabled(!shadow.enabled)}
              >
                {shadow.enabled ? t('rightSidebar.style.enabled') : t('rightSidebar.style.disabled')}
              </button>
            </div>
            {shadow.enabled && (
              <>
                <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="color-style-label">{t('rightSidebar.style.shadowColor')}</span>
                  <input
                    type="color"
                    value={shadow.color.startsWith('#') ? shadow.color.slice(0, 7) : '#000000'}
                    onChange={(e) => patchShadow({ color: e.target.value })}
                    className="color-style-input"
                    title={t('rightSidebar.style.shadowColor')}
                  />
                </div>
                <div className="property-row-full">
                  <label className="label">{t('rightSidebar.style.shadowOffsetX')}</label>
                  <div className="property-input-with-slider">
                    <input
                      type="range"
                      min={-40}
                      max={40}
                      value={shadow.offsetX}
                      onChange={(e) => patchShadow({ offsetX: Number.parseInt(e.target.value, 10) })}
                    />
                    <PropertyInput
                      className="property-input-inline"
                      value={shadow.offsetX}
                      min={-40}
                      max={40}
                      onChange={(v) => patchShadow({ offsetX: v })}
                    />
                  </div>
                </div>
                <div className="property-row-full">
                  <label className="label">{t('rightSidebar.style.shadowOffsetY')}</label>
                  <div className="property-input-with-slider">
                    <input
                      type="range"
                      min={-40}
                      max={40}
                      value={shadow.offsetY}
                      onChange={(e) => patchShadow({ offsetY: Number.parseInt(e.target.value, 10) })}
                    />
                    <PropertyInput
                      className="property-input-inline"
                      value={shadow.offsetY}
                      min={-40}
                      max={40}
                      onChange={(v) => patchShadow({ offsetY: v })}
                    />
                  </div>
                </div>
                <div className="property-row-full">
                  <label className="label">{t('rightSidebar.style.shadowBlur')}</label>
                  <div className="property-input-with-slider">
                    <input
                      type="range"
                      min={0}
                      max={60}
                      value={shadow.blur}
                      onChange={(e) => patchShadow({ blur: Number.parseInt(e.target.value, 10) })}
                    />
                    <PropertyInput
                      className="property-input-inline"
                      value={shadow.blur}
                      min={0}
                      max={60}
                      onChange={(v) => patchShadow({ blur: v })}
                    />
                  </div>
                </div>
                {isBoxShape && (
                  <div className="property-row-full">
                    <label className="label">{t('rightSidebar.style.shadowSpread')}</label>
                    <div className="property-input-with-slider">
                      <input
                        type="range"
                        min={-20}
                        max={40}
                        value={shadow.spread}
                        onChange={(e) => patchShadow({ spread: Number.parseInt(e.target.value, 10) })}
                      />
                      <PropertyInput
                        className="property-input-inline"
                        value={shadow.spread}
                        min={-20}
                        max={40}
                        onChange={(v) => patchShadow({ spread: v })}
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className="color-style-label">{t('rightSidebar.style.glow')}</span>
              <button
                type="button"
                className={`style-border-side-link ${glow.enabled ? 'active' : ''}`}
                aria-pressed={glow.enabled}
                onClick={() => setGlowEnabled(!glow.enabled)}
              >
                {glow.enabled ? t('rightSidebar.style.enabled') : t('rightSidebar.style.disabled')}
              </button>
            </div>
            {glow.enabled && (
              <>
                <div className="color-style-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="color-style-label">{t('rightSidebar.style.glowColor')}</span>
                  <input
                    type="color"
                    value={glow.color.startsWith('#') ? glow.color.slice(0, 7) : DEFAULT_OBJECT_GLOW.color}
                    onChange={(e) => patchGlow({ color: e.target.value })}
                    className="color-style-input"
                    title={t('rightSidebar.style.glowColor')}
                  />
                </div>
                <div className="property-row-full">
                  <label className="label">{t('rightSidebar.style.glowBlur')}</label>
                  <div className="property-input-with-slider">
                    <input
                      type="range"
                      min={0}
                      max={80}
                      value={glow.blur}
                      onChange={(e) => patchGlow({ blur: Number.parseInt(e.target.value, 10) })}
                    />
                    <PropertyInput
                      className="property-input-inline"
                      value={glow.blur}
                      min={0}
                      max={80}
                      onChange={(v) => patchGlow({ blur: v })}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Toolpad>
  );
}

function ObjectPropertiesToolpad() {
  const { t } = useTranslation();
  const {
    toolpads,
    toggleToolpad,
    keepAspectRatio,
    toggleKeepAspectRatio,
    stageResizeEnabled,
    stageRotateEnabled,
    toggleStageResizeEnabled,
    toggleStageRotateEnabled,
  } = useUIStore();
  const { currentScene, currentLayer, updateSceneObject, currentMovieResponse } = useMovieStore();
  const { selectedIds, focusedId, setFocused } = useSelectionStore();
  const {
    layers,
    keyframes,
    tweens,
    selectedKeyframe,
    selectedTween,
    updateKeyframe,
    addKeyframe,
    updateTween,
  } = useTimelineStore();
  const { currentFrame } = useEditorStore();
  const {
    tweenSave,
    sceneObjectPatchStateProperties,
    sceneObjectSetVisibility,
    sceneObjectSetLock,
    connect,
    status: signalRStatus,
  } = useSignalR();
  const isOpen = toolpads.objectProperties.isOpen;
  const [activeTab, setActiveTab] = useState<PropertyTab>('object');
  const [stateObjectId, setStateObjectId] = useState<string | null>(null);
  const [copyingDirection, setCopyingDirection] = useState<'previous' | 'next' | null>(null);
  const layoutPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tween seçilince tween tab; timeline seçimi kalkınca (ör. yeni çizim) object tab.
  useEffect(() => {
    if (selectedTween) {
      setActiveTab('tween');
      return;
    }
    setActiveTab('object');
  }, [selectedTween?.layerId, selectedTween?.startFrame]);

  useEffect(() => {
    return () => {
      if (layoutPersistTimerRef.current) clearTimeout(layoutPersistTimerRef.current);
    };
  }, []);

  const selectedLayerId = selectedTween?.layerId ?? selectedKeyframe?.layerId ?? currentLayer?.id ?? null;
  const selectedLayerIdNum = selectedLayerId ? Number(selectedLayerId) : NaN;

  const selectedObjectsFromScene =
    currentScene?.objects.filter((o) => selectedIds.includes(o.id)) ?? [];
  const activeSelectedId =
    focusedId && selectedIds.includes(focusedId) ? focusedId : selectedIds[0] ?? null;
  const selectedObjectFromScene = activeSelectedId
    ? selectedObjectsFromScene.find((o) => o.id === activeSelectedId) ??
      selectedObjectsFromScene[0]
    : undefined;
  const currentLayerIdNum = currentLayer ? Number(currentLayer.id) : NaN;
  const layerForSelection = selectedLayerId ? layers.find((l) => l.id === selectedLayerId) : null;
  const selectedObjectFromLayer = selectedLayerId
    ? currentScene?.objects.find(
        (o) =>
          (layerForSelection?.objectId && o.id === layerForSelection.objectId) ||
          (!Number.isNaN(selectedLayerIdNum) && o.layerId === selectedLayerIdNum)
      )
    : currentLayer
    ? currentScene?.objects.find(
        (o) =>
          o.id === currentLayer.objectId ||
          o.id === currentLayer.id ||
          (!Number.isNaN(currentLayerIdNum) && o.layerId === currentLayerIdNum)
      )
    : undefined;
  const selectedObject = selectedObjectFromScene ?? selectedObjectFromLayer;
  const objectKeyframes = selectedLayerId ? keyframes[selectedLayerId] ?? [] : [];
  const objectTweens = selectedLayerId ? tweens[selectedLayerId] ?? [] : [];
  const showSelectionSwitcher = selectedObjectsFromScene.length > 1;

  const hasObjectSelection = !!selectedObjectFromScene;
  const hasTweenSelection = !!(selectedTween && selectedLayerId);
  const hasKeyframeSelection = !!(selectedKeyframe && selectedLayerId);
  const hasTimelineSelection = hasTweenSelection || hasKeyframeSelection;
  const showProperties = hasObjectSelection || hasTimelineSelection;

  const stateTarget =
    selectedLayerId != null
      ? resolveStateTarget({
          layerId: selectedLayerId,
          currentFrame,
          layerKeyframes: objectKeyframes,
          selectedKeyframe,
        })
      : null;
  const currentKeyframe =
    stateTarget?.kind === 'keyframe' ? stateTarget.keyframe : undefined;

  const selectedKeyframeRecord =
    selectedKeyframe && selectedLayerId === selectedKeyframe.layerId
      ? objectKeyframes.find((kf) => kf.frame === selectedKeyframe.frame)
      : undefined;

  const sortedLayerKeyframes = useMemo(
    () => [...objectKeyframes].sort((a, b) => a.frame - b.frame),
    [objectKeyframes]
  );
  const selectedKeyframeIndex = selectedKeyframeRecord
    ? sortedLayerKeyframes.findIndex((kf) => kf.frame === selectedKeyframeRecord.frame)
    : -1;
  const previousKeyframe =
    selectedKeyframeIndex > 0 ? sortedLayerKeyframes[selectedKeyframeIndex - 1] : undefined;
  const nextKeyframe =
    selectedKeyframeIndex >= 0 && selectedKeyframeIndex < sortedLayerKeyframes.length - 1
      ? sortedLayerKeyframes[selectedKeyframeIndex + 1]
      : undefined;

  const keyframeStateObjects = useMemo(() => {
    if (!selectedKeyframeRecord) return [];
    const sceneObjects = currentScene?.objects ?? [];
    const rows = Object.entries(selectedKeyframeRecord.objectStates ?? {}).map(([id, state]) => {
      const obj = sceneObjects.find((item) => item.id === id);
      return { id, name: obj?.name || obj?.type || id, state };
    });
    const primaryId = layerForSelection?.objectId;
    if (primaryId && !rows.some((row) => row.id === primaryId)) {
      const obj = sceneObjects.find((item) => item.id === primaryId);
      rows.unshift({
        id: primaryId,
        name: obj?.name || obj?.type || primaryId,
        state: selectedKeyframeRecord.state,
      });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedKeyframeRecord, currentScene?.objects, layerForSelection?.objectId]);

  useEffect(() => {
    setStateObjectId(null);
  }, [selectedKeyframe?.layerId, selectedKeyframe?.frame]);

  const currentTween = selectedTween
    ? objectTweens.find((tw) => tw.startFrame === selectedTween.startFrame)
    : objectTweens.find((tw) => tw.startFrame <= currentFrame && tw.endFrame > currentFrame);

  const persistLayoutProperties = useCallback(
    async (obj: SceneObject, keyFrameId: number) => {
      const sceneObjectId = Number.parseInt(obj.id, 10);
      if (!Number.isFinite(sceneObjectId)) return;

      try {
        if (signalRStatus !== 'Connected') await connect();
        await sceneObjectPatchStateProperties({
          sceneObjectId,
          keyFrameId,
          patch: sceneObjectTransformPatchJson(obj),
        });
      } catch (error) {
        console.error('[ObjectProperties] Failed to persist layout properties:', error);
      }
    },
    [connect, sceneObjectPatchStateProperties, signalRStatus]
  );

  const schedulePersistLayout = useCallback(
    (obj: SceneObject, keyFrameId: number) => {
      if (layoutPersistTimerRef.current) clearTimeout(layoutPersistTimerRef.current);
      layoutPersistTimerRef.current = setTimeout(() => {
        void persistLayoutProperties(obj, keyFrameId);
      }, 300);
    },
    [persistLayoutProperties]
  );

  const handlePropertyChange = useCallback((prop: keyof SceneObject, value: number | string | boolean) => {
    if (!selectedObject) return;

    // Keep aspect ratio for width/height edits if enabled
    const patch: Partial<SceneObject> = { [prop]: value } as Partial<SceneObject>;
    if (keepAspectRatio && (prop === 'width' || prop === 'height')) {
      const startW = selectedObject.width;
      const startH = selectedObject.height;
      const ratio = startH > 0 ? startW / startH : 0;
      if (ratio > 0) {
        if (prop === 'width' && typeof value === 'number') {
          patch.height = Math.max(1, Math.round(value / ratio));
        }
        if (prop === 'height' && typeof value === 'number') {
          patch.width = Math.max(1, Math.round(value * ratio));
        }
      }
    }

    const isLayoutProp =
      prop === 'x' ||
      prop === 'y' ||
      prop === 'width' ||
      prop === 'height' ||
      prop === 'rotation' ||
      prop === 'opacity';

    const nextObject = { ...selectedObject, ...patch };
    const scenePatch: Partial<SceneObject> =
      stateTarget?.kind === 'default' && isLayoutProp
        ? {
            ...patch,
            defaultPose: {
              x: nextObject.x,
              y: nextObject.y,
              width: nextObject.width,
              height: nextObject.height,
              rotation: nextObject.rotation,
              opacity: nextObject.opacity,
            },
          }
        : patch;

    // Update the scene object
    updateSceneObject(selectedObject.id, scenePatch);

    // Aktif keyframe + (çoklu nesne katmanında) objectStates — Stage commitLayoutAfterTransform ile aynı mantık
    if (
      selectedLayerId &&
      currentKeyframe &&
      isLayoutProp
    ) {
      const layer = layers.find((l) => l.id === selectedLayerId);
      const isPrimary = layer?.objectId === selectedObject.id;

      const baseState: KeyframeState =
        currentKeyframe.objectStates?.[selectedObject.id] ??
        (isPrimary
          ? currentKeyframe.state
          : {
              x: selectedObject.x,
              y: selectedObject.y,
              width: selectedObject.width,
              height: selectedObject.height,
              rotation: selectedObject.rotation,
              opacity: selectedObject.opacity,
            });

      const statePatch: KeyframeState =
        prop === 'width' || prop === 'height'
          ? ({
              ...baseState,
              ...(patch.width != null ? { width: patch.width as number } : {}),
              ...(patch.height != null ? { height: patch.height as number } : {}),
            } as KeyframeState)
          : ({ ...baseState, [prop]: value as number } as KeyframeState);

      if (isPrimary) {
        const objectStates = currentKeyframe.objectStates
          ? { ...currentKeyframe.objectStates, [selectedObject.id]: statePatch }
          : undefined;
        updateKeyframe(selectedLayerId, currentKeyframe.frame, {
          state: statePatch,
          ...(objectStates ? { objectStates } : {}),
        });
      } else {
        updateKeyframe(selectedLayerId, currentKeyframe.frame, {
          objectStates: { ...(currentKeyframe.objectStates ?? {}), [selectedObject.id]: statePatch },
        });
      }
    }

    // Stage drag/resize ile aynı: layout alanlarını state properties'e yaz.
    if (isLayoutProp) {
      const keyFrameId =
        stateTarget?.kind === 'keyframe' ? stateTarget.keyframeId : 0;
      if (stateTarget?.kind === 'keyframe' && keyFrameId == null) return;
      schedulePersistLayout(nextObject, keyFrameId ?? 0);
    }

    if (prop === 'visible' && typeof value === 'boolean') {
      const parsedId = Number(selectedObject.id);
      if (!Number.isFinite(parsedId)) return;
      void (async () => {
        try {
          if (signalRStatus !== 'Connected') {
            await connect();
          }
          await sceneObjectSetVisibility({ id: parsedId, isVisible: value });
        } catch (error) {
          console.error('[ObjectProperties] Failed to persist visibility:', error);
          updateSceneObject(selectedObject.id, { visible: !value });
        }
      })();
    }

    if (prop === 'locked' && typeof value === 'boolean') {
      const parsedId = Number(selectedObject.id);
      if (!Number.isFinite(parsedId)) return;
      void (async () => {
        try {
          if (signalRStatus !== 'Connected') {
            await connect();
          }
          await sceneObjectSetLock({ id: parsedId, isLocked: value });
        } catch (error) {
          console.error('[ObjectProperties] Failed to persist lock:', error);
          updateSceneObject(selectedObject.id, { locked: !value });
        }
      })();
    }
  }, [
    selectedObject,
    selectedLayerId,
    layers,
    updateSceneObject,
    currentKeyframe,
    updateKeyframe,
    keepAspectRatio,
    stateTarget,
    schedulePersistLayout,
    signalRStatus,
    connect,
    sceneObjectSetVisibility,
    sceneObjectSetLock,
  ]);

  const handleEasingChange = async (easing: EasingType) => {
    if (!currentTween || !selectedLayerId || !currentScene || !currentMovieResponse) return;

    const previousEasing = currentTween.easing;
    updateTween(currentTween.id, { easing });

    const tweenId = parseInt(currentTween.id, 10);
    const layerIdNum = parseInt(selectedLayerId, 10);
    if (isNaN(tweenId) || isNaN(layerIdNum)) return;

    const layerKeyframes = keyframes[selectedLayerId] ?? [];
    const startKf = layerKeyframes.find((kf) => kf.frame === currentTween.startFrame);
    const endKf = layerKeyframes.find((kf) => kf.frame === currentTween.endFrame);
    if (!startKf || !endKf) return;

    const startKeyframeId = parseInt(startKf.id, 10);
    const endKeyframeId = parseInt(endKf.id, 10);
    if (isNaN(startKeyframeId) || isNaN(endKeyframeId)) return;

    try {
      await tweenSave({
        id: tweenId,
        movieId: currentMovieResponse.id,
        sceneId: parseInt(currentScene.id, 10),
        layerId: layerIdNum,
        startKeyFrameId: startKeyframeId,
        endKeyFrameId: endKeyframeId,
        easing,
        name: '',
        description: '',
      });
    } catch (error) {
      console.error('Failed to save tween easing:', error);
      updateTween(currentTween.id, { easing: previousEasing });
    }
  };

  const handleCreateKeyframe = () => {
    if (!selectedObject || !selectedLayerId || !currentScene) return;

    const layer = layers.find((l) => l.id === selectedLayerId);
    if (!layer) return;

    const layerKfs = keyframes[selectedLayerId] ?? [];
    const { state, objectStates } = seedNewKeyframeState(
      layer,
      currentFrame,
      layerKfs,
      currentScene
    );

    const newKeyframe: Keyframe = {
      id: generateId('kf'),
      layerId: selectedLayerId,
      frame: currentFrame,
      state,
      ...(objectStates ? { objectStates } : {}),
    };

    addKeyframe(newKeyframe);
  };

  const copyStateToNeighbor = useCallback(
    async (direction: 'previous' | 'next') => {
      const source = selectedKeyframeRecord;
      const target = direction === 'previous' ? previousKeyframe : nextKeyframe;
      if (!source || !target || !selectedLayerId || !stateObjectId || copyingDirection) return;

      const layer = layers.find((item) => item.id === selectedLayerId);
      const sourceState =
        source.objectStates?.[stateObjectId] ??
        (layer?.objectId === stateObjectId ? source.state : undefined);
      if (!sourceState) return;

      const copied: KeyframeState = { ...sourceState };
      const isPrimary = layer?.objectId === stateObjectId;
      const previousTargetState = target.state;
      const previousTargetObjectStates = target.objectStates;

      updateKeyframe(selectedLayerId, target.frame, {
        ...(isPrimary ? { state: copied } : {}),
        objectStates: { ...(target.objectStates ?? {}), [stateObjectId]: copied },
      });

      const sceneObjectId = Number.parseInt(stateObjectId, 10);
      const keyFrameId = Number.parseInt(target.id, 10);
      if (!Number.isFinite(sceneObjectId) || !Number.isFinite(keyFrameId)) return;

      setCopyingDirection(direction);
      try {
        if (signalRStatus !== 'Connected') await connect();
        await sceneObjectPatchStateProperties({
          sceneObjectId,
          keyFrameId,
          patch: keyframeStatePatchJson(copied),
        });
      } catch (error) {
        console.error('[ObjectProperties] Failed to copy keyframe state:', error);
        updateKeyframe(selectedLayerId, target.frame, {
          state: previousTargetState,
          objectStates: previousTargetObjectStates,
        });
      } finally {
        setCopyingDirection(null);
      }
    },
    [
      selectedKeyframeRecord,
      previousKeyframe,
      nextKeyframe,
      selectedLayerId,
      stateObjectId,
      copyingDirection,
      layers,
      updateKeyframe,
      signalRStatus,
      connect,
      sceneObjectPatchStateProperties,
    ]
  );

  return (
    <Toolpad
      title={t('rightSidebar.properties.title')}
      isOpen={isOpen}
      onToggle={() => toggleToolpad('objectProperties')}
    >
      <div className="object-properties">
        {showSelectionSwitcher && (
          <div className="property-section properties-selection-switcher">
            <div className="property-section-title">
              {t('rightSidebar.properties.selectedObjects', {
                count: selectedObjectsFromScene.length,
              })}
            </div>
            <Select.Root
              value={selectedObjectFromScene?.id ?? ''}
              onValueChange={setFocused}
              size="1"
            >
              <Select.Trigger
                variant="surface"
                className="style-select-trigger"
                aria-label={t('rightSidebar.properties.selectedObjectAria')}
              />
              <Select.Content position="popper" sideOffset={4}>
                {selectedObjectsFromScene.map((obj) => (
                  <Select.Item key={obj.id} value={obj.id}>
                    {obj.name || obj.type}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
        )}

        {showProperties ? (
          <>
            {(selectedObject && hasTimelineSelection) && (
              <div className="properties-tabs">
                <button
                  className={`properties-tab ${activeTab === 'object' ? 'active' : ''}`}
                  onClick={() => setActiveTab('object')}
                >
{t('rightSidebar.properties.object')}
                </button>
                <button
                  className={`properties-tab ${activeTab === 'tween' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tween')}
                >
{t('rightSidebar.properties.tween')}
                </button>
              </div>
            )}

            {activeTab === 'object' && selectedObject && (
              <>
                <div className="property-section">
                  <div className="property-section-title">{t('rightSidebar.properties.stageTools')}</div>
                  <div className="properties-stage-toggles">
                    <button
                      type="button"
                      className={`properties-stage-toggle ${stageResizeEnabled ? 'active' : ''}`}
                      aria-pressed={stageResizeEnabled}
                      title={t('rightSidebar.properties.stageResizeHint')}
                      onClick={toggleStageResizeEnabled}
                    >
                      {t('rightSidebar.properties.stageResize')}
                    </button>
                    <button
                      type="button"
                      className={`properties-stage-toggle ${stageRotateEnabled ? 'active' : ''}`}
                      aria-pressed={stageRotateEnabled}
                      title={t('rightSidebar.properties.stageRotateHint')}
                      onClick={toggleStageRotateEnabled}
                    >
                      {t('rightSidebar.properties.stageRotate')}
                    </button>
                  </div>
                </div>

                <div className="property-section">
                  <div className="property-section-title">{t('rightSidebar.properties.transform')}</div>
                  <div className="property-row-full" style={{ marginBottom: 8 }}>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={keepAspectRatio}
                        onChange={toggleKeepAspectRatio}
                      />
{t('rightSidebar.properties.keepAspectRatio')}
                    </label>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
{t('rightSidebar.properties.keepAspectRatioHint')}
                    </div>
                  </div>
                  <div className="property-grid">
                    <PropertyInput
                      label="X"
                      value={selectedObject.x}
                      onChange={(v) => handlePropertyChange('x', v)}
                    />
                    <PropertyInput
                      label="Y"
                      value={selectedObject.y}
                      onChange={(v) => handlePropertyChange('y', v)}
                    />
                    <PropertyInput
                      label="W"
                      value={selectedObject.width}
                      onChange={(v) => handlePropertyChange('width', v)}
                      min={1}
                    />
                    <PropertyInput
                      label="H"
                      value={selectedObject.height}
                      onChange={(v) => handlePropertyChange('height', v)}
                      min={1}
                    />
                  </div>
                </div>

                <div className="property-section">
                  <div className="property-section-title">{t('rightSidebar.properties.appearance')}</div>
                  <div className="property-row-full">
                    <label className="label">{t('rightSidebar.properties.rotation')}</label>
                    <div className="property-input-with-slider">
                      <input
                        type="range"
                        min={-180}
                        max={180}
                        value={selectedObject.rotation}
                        onChange={(e) => handlePropertyChange('rotation', parseInt(e.target.value))}
                        aria-label={t('rightSidebar.properties.rotationAria')}
                      />
                      <PropertyInput
                        className="property-input-inline"
                        value={selectedObject.rotation}
                        min={-180}
                        max={180}
                        onChange={(v) => handlePropertyChange('rotation', v)}
                        aria-label={t('rightSidebar.properties.rotationDegreesAria')}
                      />
                      <span className="unit">°</span>
                    </div>
                  </div>
                  <div className="property-row-full">
                    <label className="label">{t('rightSidebar.properties.opacity')}</label>
                    <div className="property-input-with-slider">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={selectedObject.opacity * 100}
                        onChange={(e) => handlePropertyChange('opacity', parseInt(e.target.value) / 100)}
                        aria-label={t('rightSidebar.properties.opacityAria')}
                      />
                      <PropertyInput
                        className="property-input-inline"
                        value={Math.round(selectedObject.opacity * 100)}
                        min={0}
                        max={100}
                        onChange={(v) => handlePropertyChange('opacity', v / 100)}
                        aria-label={t('rightSidebar.properties.opacityPercentAria')}
                      />
                      <span className="unit">%</span>
                    </div>
                  </div>
                </div>

                <div className="property-section">
                  <div className="property-section-title">{t('rightSidebar.properties.visibility')}</div>
                  <div className="property-checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedObject.visible}
                        onChange={(e) => handlePropertyChange('visible', e.target.checked)}
                      />
{t('common.visible')}
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedObject.locked}
                        onChange={(e) => handlePropertyChange('locked', e.target.checked)}
                      />
{t('common.locked')}
                    </label>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'tween' && (
              <>
                {currentTween ? (
                  <div className="property-section">
                    <div className="property-section-title">
                      {t('rightSidebar.properties.activeTween', {
                        start: currentTween.startFrame,
                        end: currentTween.endFrame,
                      })}
                    </div>
                    <div className="property-row-full">
                      <span className="label" id="easing-options-label">
                        {t('rightSidebar.properties.easing')}
                      </span>
                      <div
                        className="easing-options"
                        role="listbox"
                        aria-labelledby="easing-options-label"
                        aria-label={t('rightSidebar.properties.easingAria')}
                      >
                        {EASING_OPTIONS.map((easing) => {
                          const selected = currentTween.easing === easing;
                          return (
                            <button
                              key={easing}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={`easing-option${selected ? ' selected' : ''}`}
                              onClick={() => {
                                if (!selected) void handleEasingChange(easing);
                              }}
                            >
                              {t(EASING_LABEL_KEYS[easing])}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="easing-preview">
                      <EasingPreview easing={currentTween.easing} />
                    </div>
                  </div>
                ) : (
                  <div className="no-tween-message">
<p>{t('rightSidebar.properties.noTween')}</p>
<p className="hint">{t('rightSidebar.properties.noTweenHint')}</p>
                  </div>
                )}

                {objectTweens.length > 0 && (
                  <div className="property-section">
                    <div className="property-section-title">{t('rightSidebar.properties.allTweens')}</div>
                    <div className="tween-list">
                      {objectTweens.map((tw) => (
                        <div 
                          key={tw.id} 
                          className={`tween-item ${tw === currentTween ? 'active' : ''}`}
                        >
                          <span>{t('rightSidebar.properties.frameRange', { start: tw.startFrame, end: tw.endFrame })}</span>
                          <span className="tween-easing">{tw.easing}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="no-selection-message">
{t('rightSidebar.properties.noSelection')}
          </div>
        )}

        <div className="property-section">
          <div className="property-section-title">{t('rightSidebar.properties.keyframe')}</div>
          <div className="keyframe-info">
            <div className="keyframe-frame-info">
              <span className="keyframe-frame-label">{t('rightSidebar.properties.frame')}</span>
              <span className="keyframe-frame-number">{currentFrame}</span>
            </div>
            {currentKeyframe ? (
              <span className="keyframe-badge">{t('rightSidebar.properties.activeKeyframe', { frame: currentKeyframe.frame })}</span>
            ) : (
              <span className="no-keyframe">{t('rightSidebar.properties.noKeyframe')}</span>
            )}
            <button
              className="btn btn-small"
              onClick={handleCreateKeyframe}
              disabled={!hasKeyframeSelection || !selectedObject || !selectedLayerId}
              title={!hasKeyframeSelection ? t('rightSidebar.properties.selectFrameHint') : undefined}
            >
{currentKeyframe ? t('rightSidebar.properties.updateKeyframe') : t('rightSidebar.properties.createKeyframe')}
            </button>
            {selectedKeyframeRecord && (
              <div className="keyframe-state-objects">
                <div className="property-section-title">{t('rightSidebar.properties.stateObjects')}</div>
                {keyframeStateObjects.length === 0 ? (
                  <span className="no-keyframe">{t('rightSidebar.properties.noStateObjects')}</span>
                ) : (
                  keyframeStateObjects.map((row) => {
                    const selected = stateObjectId === row.id;
                    return (
                      <div
                        key={row.id}
                        className={`keyframe-state-object${selected ? ' selected' : ''}`}
                      >
                        <button
                          type="button"
                          className="keyframe-state-object-name"
                          aria-pressed={selected}
                          onClick={() => setStateObjectId(selected ? null : row.id)}
                        >
                          {row.name}
                        </button>
                        {selected && (
                          <div className="keyframe-state-actions">
                            <button
                              type="button"
                              className="btn btn-tiny"
                              disabled={!previousKeyframe || copyingDirection != null}
                              title={
                                previousKeyframe
                                  ? t('rightSidebar.properties.copyStateToPrevious', {
                                      frame: previousKeyframe.frame,
                                    })
                                  : t('rightSidebar.properties.noPreviousKeyframe')
                              }
                              onClick={() => void copyStateToNeighbor('previous')}
                            >
                              {previousKeyframe
                                ? t('rightSidebar.properties.copyStateToPrevious', {
                                    frame: previousKeyframe.frame,
                                  })
                                : t('rightSidebar.properties.noPreviousKeyframe')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-tiny"
                              disabled={!nextKeyframe || copyingDirection != null}
                              title={
                                nextKeyframe
                                  ? t('rightSidebar.properties.copyStateToNext', {
                                      frame: nextKeyframe.frame,
                                    })
                                  : t('rightSidebar.properties.noNextKeyframe')
                              }
                              onClick={() => void copyStateToNeighbor('next')}
                            >
                              {nextKeyframe
                                ? t('rightSidebar.properties.copyStateToNext', {
                                    frame: nextKeyframe.frame,
                                  })
                                : t('rightSidebar.properties.noNextKeyframe')}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Toolpad>
  );
}



function EasingPreview({ easing }: { easing: EasingType }) {
  // Simple SVG preview of easing curve
  const getPath = (type: EasingType): string => {
    switch (type) {
      case 'linear':
        return 'M 0 40 L 60 0';
      case 'easeIn':
        return 'M 0 40 Q 30 40 60 0';
      case 'easeOut':
        return 'M 0 40 Q 30 0 60 0';
      case 'easeInOut':
        return 'M 0 40 C 20 40, 40 0, 60 0';
      case 'bounce':
        return 'M 0 40 Q 20 0 30 20 Q 40 40 45 30 Q 50 20 60 0';
      case 'elastic':
        return 'M 0 40 Q 10 45 20 35 Q 30 -5 40 10 Q 50 0 60 0';
      case 'back':
        return 'M 0 40 Q 15 50 30 20 Q 45 -10 60 0';
      default:
        return 'M 0 40 L 60 0';
    }
  };

  return (
    <svg width="60" height="40" className="easing-svg">
      <path
        d={getPath(easing)}
        fill="none"
        stroke="#4cff00"
        strokeWidth="2"
      />
    </svg>
  );
}



function AlignToolpad() {
  const { t } = useTranslation();
  const { toolpads, toggleToolpad } = useUIStore();
  const { currentScene, updateSceneObject, currentMovie } = useMovieStore();
  const { selectedIds } = useSelectionStore();
  const isOpen = toolpads.alignPad?.isOpen ?? false;

  const selectedObject = currentScene?.objects.find((o) => selectedIds.includes(o.id));
  const stageWidth = currentMovie?.width ?? 800;
  const stageHeight = currentMovie?.height ?? 600;

  const alignLeft = () => {
    if (selectedObject) {
      updateSceneObject(selectedObject.id, { x: 0 });
    }
  };

  const alignCenter = () => {
    if (selectedObject) {
      updateSceneObject(selectedObject.id, {
        x: Math.round((stageWidth - selectedObject.width) / 2),
      });
    }
  };

  const alignRight = () => {
    if (selectedObject) {
      updateSceneObject(selectedObject.id, { x: stageWidth - selectedObject.width });
    }
  };

  const alignTop = () => {
    if (selectedObject) {
      updateSceneObject(selectedObject.id, { y: 0 });
    }
  };

  const alignMiddle = () => {
    if (selectedObject) {
      updateSceneObject(selectedObject.id, {
        y: Math.round((stageHeight - selectedObject.height) / 2),
      });
    }
  };

  const alignBottom = () => {
    if (selectedObject) {
      updateSceneObject(selectedObject.id, { y: stageHeight - selectedObject.height });
    }
  };

  return (
    <Toolpad
      title={t('rightSidebar.align.title')}
      isOpen={isOpen}
      onToggle={() => toggleToolpad('alignPad')}
    >
      <div className="align-pad">
        <div className="align-group">
          <div className="align-label">{t('rightSidebar.align.horizontal')}</div>
          <div className="align-buttons">
            <button
              type="button"
              className="align-btn"
              onClick={alignLeft}
              disabled={!selectedObject}
              title={t('rightSidebar.align.left')}
              aria-label={t('rightSidebar.align.left')}
            >
              <AlignLeftIcon width={16} height={16} />
            </button>
            <button
              type="button"
              className="align-btn"
              onClick={alignCenter}
              disabled={!selectedObject}
              title={t('rightSidebar.align.center')}
              aria-label={t('rightSidebar.align.center')}
            >
              <AlignCenterHorizontallyIcon width={16} height={16} />
            </button>
            <button
              type="button"
              className="align-btn"
              onClick={alignRight}
              disabled={!selectedObject}
              title={t('rightSidebar.align.right')}
              aria-label={t('rightSidebar.align.right')}
            >
              <AlignRightIcon width={16} height={16} />
            </button>
          </div>
        </div>
        <div className="align-group">
          <div className="align-label">{t('rightSidebar.align.vertical')}</div>
          <div className="align-buttons">
            <button
              type="button"
              className="align-btn"
              onClick={alignTop}
              disabled={!selectedObject}
              title={t('rightSidebar.align.top')}
              aria-label={t('rightSidebar.align.top')}
            >
              <AlignTopIcon width={16} height={16} />
            </button>
            <button
              type="button"
              className="align-btn"
              onClick={alignMiddle}
              disabled={!selectedObject}
              title={t('rightSidebar.align.middle')}
              aria-label={t('rightSidebar.align.middle')}
            >
              <AlignCenterVerticallyIcon width={16} height={16} />
            </button>
            <button
              type="button"
              className="align-btn"
              onClick={alignBottom}
              disabled={!selectedObject}
              title={t('rightSidebar.align.bottom')}
              aria-label={t('rightSidebar.align.bottom')}
            >
              <AlignBottomIcon width={16} height={16} />
            </button>
          </div>
        </div>
      </div>
    </Toolpad>
  );
}
